import classNames from 'classnames';
import {
  DateInput,
  FlexContainer,
  Txt,
  TitledBlock,
  Input,
  Button,
  Spinner,
  ErrorMessage,
  View,
  PartialWidthContainer,
  TxtTitle,
  ContentBlock,
  Checkbox,
  Icon,
  SupportBlock,
} from 'components';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { tenancyDefault } from 'config/dataDefaults';
import { useProperty, useDataActions } from 'state';
import { DurationSelect, InputTenants } from './bricks';
import TenancyAddEditState from './TenancyAddEditState';
import styles from './TenancyAddEditScreen.module.scss';

/**
 * TenancyAddEditScreen
 *
 * @return {JSX.Element}
 */
const TenancyAddEditScreen = ({ className }) => {
  const navigate = useNavigate();
  const storageActions = useDataActions();
  const propertyDoc = useProperty();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  // const tenancyDoc = useTenancy();
  const isEdit = false;
  const tenancyAddEditState = useMemo(
    () => new TenancyAddEditState(tenancyDefault()),
    []
  );

  const [tenantsState, setTenantsState] = useState(() => []);

  const handleChange = ({ target: { name, value } }) => {
    setError('');
    tenancyAddEditState.setState(name, value);
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleSubmit = (e) => {
    console.log('Submit', tenancyAddEditState.state);
    e.preventDefault();
    setIsLoading(true);
    storageActions.tenancy
      .createTenancy(propertyDoc, toJS(tenancyAddEditState.state), tenantsState)
      .then(() => handleBack())
      .catch((e) => {
        setError(e.message);
        setIsLoading(false);
      });
  };

  const { state: dataState } = tenancyAddEditState;

  const handleChangeTenants = ({ target: { value } }) => {
    setTenantsState(value);
  };

  return (
    <PartialWidthContainer
      className={classNames(
        styles.TenancyAddEditScreen,
        className,
        TenancyAddEditScreen.displayName
      )}
      column
      gap
      small
    >
      <FlexContainer>
        <Button onClick={handleBack}>
          <Icon s name="goback" /> Back
        </Button>
      </FlexContainer>
      <ContentBlock column>
        <View>
          <TxtTitle xxxx>Enter details about the tenancy</TxtTitle>
        </View>
        <View>
          <SupportBlock />
        </View>
      </ContentBlock>
      <form onSubmit={handleSubmit}>
        <TitledBlock title="Tenants">
          <InputTenants
            name="tenants"
            reverse
            required
            value={tenantsState}
            onChange={handleChangeTenants}
          />
        </TitledBlock>
        <TitledBlock title="When does this tenancy start?">
          <DateInput
            minDate={tenancyAddEditState.tenancyMinDate}
            filterDate={(date) => {
              return date.getDate() <= 28;
            }}
            name="dates.startDate"
            customLabel=" "
            onChange={handleChange}
            value={
              dataState.dates?.startDate
                ? dataState.dates.startDate
                : tenancyAddEditState.tenancyMinDate
            }
            asInput
            required
            disabled={isLoading}
          />
          <View className={styles.Description}>
            <Txt regular>
              For the purposes of accounting our banking partner requires
              monthly payments to be made on the 1st to the 28th of a month.
            </Txt>
          </View>
        </TitledBlock>
        <TitledBlock title="What is the duration of this tenancy?">
          <DurationSelect
            name="durationInMonths"
            value={dataState.durationInMonths}
            onChange={handleChange}
            required
            disabled={isLoading}
          />
        </TitledBlock>
        <TitledBlock title="Enter monthly rent">
          <Input
            className={styles.InputNumber}
            pre="£"
            min="100"
            max="50000"
            maxLength="8"
            step="0.01"
            type="number"
            name="payments.rentAmount"
            value={dataState.payments?.rentAmount || ''}
            onChange={handleChange}
            fullWidth
            required
            disabled={isLoading}
          />
        </TitledBlock>
        <TitledBlock title="Deposit to be paid">
          <Input
            className={styles.InputNumber}
            pre="£"
            min="100"
            max="50000"
            maxLength="8"
            step="0.01"
            type="number"
            name="payments.depositAmount"
            value={dataState.payments?.depositAmount || ''}
            onChange={handleChange}
            fullWidth
            required
            disabled={isLoading}
          />
        </TitledBlock>
        <FlexContainer column gap>
          {error ? <ErrorMessage description={error} /> : null}
          <FlexContainer column gap>
            <Checkbox name="tenantFeesAgree" required rounded onSurface>
              I will abide to the provisions of the Tenant Fees Act 2019 which
              bans unfair fees on tenants.
            </Checkbox>
            <Checkbox name="depositAgree" required rounded onSurface>
              The deposit will be protected in a government authorised tenancy
              deposit scheme.
            </Checkbox>
            <Checkbox name="unkeepAgree" required rounded onSurface>
              I will maintain and ensure the upkeep of the property throughout
              the tenancy.
            </Checkbox>
            <Checkbox name="propaAgree" required rounded onSurface>
              Propa does not take responsibility for the legal obligations of
              either the landlord, tenant or guarantor.
            </Checkbox>
            <Button flex type="submit" big success disabled={isLoading}>
              {isLoading ? (
                <Spinner />
              ) : (
                <FlexContainer spaceBetween alignCenter flex>
                  <Txt xx>{isEdit ? 'Update' : 'Create'} Tenancy</Txt>
                  <Txt>🎉</Txt>
                </FlexContainer>
              )}
            </Button>
          </FlexContainer>
        </FlexContainer>
      </form>
    </PartialWidthContainer>
  );
};

TenancyAddEditScreen.displayName = 'TenancyAddEditScreen';

export default observer(TenancyAddEditScreen);
